import { useState, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Auto-infer geometry type from parsed item unit/section
function inferGeometryType(item) {
  const section = (item.section || '').toLowerCase();
  const unit = (item.unit || '').toUpperCase();

  if (item.botanicalName || item.commonName || section.includes('plant')) return 'point';
  if (unit === 'LF') return 'polyline';
  if (unit === 'SF' || unit === 'SY') return 'polygon';
  if (unit === 'EA') return 'point';
  return null; // user must pick
}

// Compute status from placements array
function computeStatus(item) {
  const count = (item.placements || []).length;
  if (count === 0) return 'queued';
  const parsedQty = item.parsedQty || 0;
  if (parsedQty <= 0) return 'placed';
  if (item.geometryType === 'point') {
    return count >= parsedQty ? 'placed' : 'partial';
  }
  const totalMeasured = item.placements.reduce((sum, p) => sum + (p.measuredQty || 0), 0);
  return totalMeasured >= parsedQty ? 'placed' : 'partial';
}

// Section → color map
const SECTION_COLORS = {
  Planting: '#4caf50',
  Hardscape: '#795548',
  Irrigation: '#2196f3',
  Lighting: '#ff9800',
  Materials: '#9e9e9e',
  default: '#607d8b',
};

export function getSectionColor(section) {
  return SECTION_COLORS[section] || SECTION_COLORS.default;
}

/**
 * Normalise raw rows from parseResult into placement queue items.
 *
 * Each item gets:
 *   itemId        – stable UUID
 *   sourceRef     – { sheet, table, row } indices for traceability
 *   description   – human label (commonName > botanicalName > description)
 *   parsedQty     – numeric quantity from the schedule
 *   parsedUnit    – unit string (EA / SF / LF / …)
 *   section       – Planting | Hardscape | Irrigation | …
 *   size          – size string from schedule (e.g. "24" Box")
 *   geometryType  – 'point' | 'polyline' | 'polygon' | null
 *   status        – 'queued' | 'partial' | 'placed' | 'skipped'
 *   pixelPaths    – array of saved placements (each is Array<{x,y}>)
 *   measuredQty   – measured area/length/count from drawings
 *   placedCount   – for point items: how many individual placements
 *   color         – section color hex
 */
function buildItems(parseResult, savedItems) {
  // If there are saved items, use them directly (they already have itemIds)
  if (savedItems && savedItems.length > 0) {
    return savedItems.map(item => {
      const sr = item.sourceRef || {};
      if (sr.sheet == null && sr.sheetIdx != null) {
        sr.sheet = sr.sheetIdx;
        sr.table = sr.tableIdx;
        sr.row = sr.rowIdx;
      }
      return { ...item, sourceRef: sr, color: getSectionColor(item.section) };
    });
  }

  if (!parseResult) return [];

  const items = [];

  (parseResult.sheets || []).forEach((sheet, sheetIdx) => {
    (sheet.tables || []).forEach((table, tableIdx) => {
      (table.rows || []).forEach((row, rowIdx) => {
        const qty = parseFloat(row.quantity || row.qty) || 0;
        const description =
          row.commonName ||
          row.botanicalName ||
          row.description ||
          row.material ||
          `Item ${rowIdx + 1}`;

        const section = row.section || table.section || 'Other';
        const geometryType = inferGeometryType(row);

        items.push({
          itemId: uuidv4(),
          sourceRef: { sheet: sheetIdx, table: tableIdx, row: rowIdx },
          description,
          botanicalName: row.botanicalName || '',
          commonName: row.commonName || '',
          parsedQty: qty,
          parsedUnit: (row.unit || '').toUpperCase(),
          section,
          size: row.size || '',
          geometryType,
          status: 'queued',
          placements: [],
          color: getSectionColor(section),
          symbolDataUrl: null,
          building: null,
        });
      });
    });
  });

  return items;
}

/**
 * usePlanItems — manages the placement queue state for Plan Takeoff.
 *
 * @param {object|null} parseResult  - Parsed schedule data from the plan PDF.
 * @param {Array|null}  savedItems   - Previously saved items (restores session).
 * @returns {object} Queue state + action callbacks.
 */
export default function usePlanItems(parseResult, savedItems) {
  const [items, setItems] = useState(() => buildItems(parseResult, savedItems));
  const [activeItemId, setActiveItemId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Rebuild items when parseResult first becomes available (async load)
  // Only runs once — after that, items are managed by user actions
  if (!initialized && parseResult && items.length === 0) {
    const built = buildItems(parseResult, savedItems);
    if (built.length > 0) {
      setItems(built);
      setInitialized(true);
    }
  }

  // ----- actions -----

  const addPlacement = useCallback((itemId, placement) => {
    setItems(prev => prev.map(item => {
      if (item.itemId !== itemId) return item;
      const updated = { ...item, placements: [...(item.placements || []), placement] };
      updated.status = computeStatus(updated);
      return updated;
    }));
  }, []);

  const updatePlacement = useCallback((itemId, placementId, updates) => {
    setItems(prev => prev.map(item => {
      if (item.itemId !== itemId) return item;
      const updated = {
        ...item,
        placements: (item.placements || []).map(p => p.id === placementId ? { ...p, ...updates } : p),
      };
      updated.status = computeStatus(updated);
      return updated;
    }));
  }, []);

  const removePlacement = useCallback((itemId, placementId) => {
    setItems(prev => prev.map(item => {
      if (item.itemId !== itemId) return item;
      const updated = { ...item, placements: (item.placements || []).filter(p => p.id !== placementId) };
      updated.status = computeStatus(updated);
      return updated;
    }));
  }, []);

  const skipItem = useCallback((itemId) => {
    setItems(prev => prev.map(item =>
      item.itemId !== itemId ? item : { ...item, status: 'skipped' }
    ));
    // If the skipped item was active, clear selection
    setActiveItemId(prev => prev === itemId ? null : prev);
  }, []);

  const resetItem = useCallback((itemId) => {
    setItems(prev => prev.map(item =>
      item.itemId !== itemId ? item : {
        ...item,
        status: 'queued',
        placements: [],
      }
    ));
  }, []);

  const setItemBuilding = useCallback((itemId, buildingName) => {
    setItems(prev => prev.map(item =>
      item.itemId !== itemId ? item : { ...item, building: buildingName }
    ));
  }, []);

  const setItemSymbol = useCallback((itemId, dataUrl) => {
    setItems(prev => prev.map(item =>
      item.itemId !== itemId ? item : { ...item, symbolDataUrl: dataUrl }
    ));
  }, []);

  const updateItemGeometryType = useCallback((itemId, newType) => {
    setItems(prev => prev.map(item =>
      item.itemId !== itemId ? item : { ...item, geometryType: newType }
    ));
  }, []);

  // ----- derived -----

  const groupedItems = useMemo(() => {
    const groups = {};
    items.forEach(item => {
      const sec = item.section || 'Other';
      if (!groups[sec]) {
        groups[sec] = {
          section: sec,
          color: getSectionColor(sec),
          items: [],
          placedCount: 0,
          totalCount: 0,
        };
      }
      groups[sec].items.push(item);
      groups[sec].totalCount += 1;
      if (item.status === 'placed' || item.status === 'partial') {
        groups[sec].placedCount += 1;
      }
    });
    return Object.values(groups);
  }, [items]);

  const summary = useMemo(() => {
    let totalPlaced = 0;
    let totalQueued = 0;
    let totalSkipped = 0;
    let sfTotal = 0;
    let lfTotal = 0;
    let pointsTotal = 0;

    items.forEach(item => {
      if (item.status === 'placed') totalPlaced++;
      else if (item.status === 'skipped') totalSkipped++;
      else totalQueued++;

      if (item.status === 'placed' || item.status === 'partial') {
        const placements = item.placements || [];
        const unit = item.parsedUnit || '';
        if (unit === 'EA' || item.geometryType === 'point') {
          pointsTotal += placements.length;
        } else {
          const total = placements.reduce((sum, p) => sum + (p.measuredQty || 0), 0);
          if (unit === 'SF' || unit === 'SY') sfTotal += total;
          else if (unit === 'LF') lfTotal += total;
        }
      }
    });

    return { totalPlaced, totalQueued, totalSkipped, sfTotal, lfTotal, pointsTotal };
  }, [items]);

  return {
    items,
    setItems,
    skipItem,
    resetItem,
    updateItemGeometryType,
    setItemSymbol,
    setItemBuilding,
    addPlacement,
    updatePlacement,
    removePlacement,
    groupedItems,
    summary,
    activeItemId,
    setActiveItemId,
  };
}
